import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  ogImage?: string;
  type?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

const DOMAIN = "https://tempstoragexpress.com";
const DEFAULT_OG_IMAGE = `${DOMAIN}/images/og-default.jpg`;

const SEOHead = ({
  title = "TSX | Temporary Grain Storage Systems",
  description = "TSX engineers temporary grain storage: steel bunker walls, aeration, tarps, safety equipment, and complete systems built for maximum bushel capacity.",
  canonicalPath,
  ogImage = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
  noindex = false,
}: SEOHeadProps) => {
  const location = useLocation();
  const path = canonicalPath ?? location.pathname;
  const canonical = `${DOMAIN}${path}`;

  useEffect(() => {
    // Title
    document.title = title;

    // Meta tags
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", canonical);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:card", "summary_large_image");

    // Robots directive
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    if (ogImage) {
      const absoluteOg = ogImage.startsWith("http") ? ogImage : `${DOMAIN}${ogImage}`;
      setMeta("property", "og:image", absoluteOg);
      setMeta("name", "twitter:image", absoluteOg);
    }

    // Canonical link
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);

    // JSON-LD (supports single object or array of objects)
    document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());

    const ld = jsonLd ?? {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "TSX - Temp Storage Express",
      url: DOMAIN,
      logo: `${DOMAIN}/favicon.ico`,
      description: "Innovative temporary grain storage systems including steel bunker walls, aeration, and safety equipment.",
      address: {
        "@type": "PostalAddress",
        streetAddress: "2200 Gannett Avenue",
        addressLocality: "Des Moines",
        addressRegion: "IA",
        postalCode: "50321",
        addressCountry: "US",
      },
      telephone: "515-380-9000",
      email: "sales@apexindustrialco.com",
      sameAs: [
        "https://www.facebook.com/ApexIndustrial1/",
        "https://x.com/IndustrialAPEX",
        "https://www.linkedin.com/company/apex-industrial",
      ],
    };

    const ldArray = Array.isArray(ld) ? ld : [ld];
    ldArray.forEach((entry) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-jsonld", "true");
      script.textContent = JSON.stringify(entry);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
    };
  }, [title, description, canonical, ogImage, type, jsonLd, noindex]);

  return null;
};

export default SEOHead;
